import { useMemo } from "react";
import { useQuery } from "@apollo/client";
import { LOAD_NOTIFICATION_SETTINGS_QUERY } from "globals/graphql";
import { NotificationSetting, TextRecipientEnum } from "types";

const { None } = TextRecipientEnum;

// Notification name constants
const {
  SALES_AUTOMATION_ENROLLMENT,
  SALES_AUTOMATION_1ST_REMINDER,
  SALES_AUTOMATION_2ND_REMINDER,
  DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER,
  DRIVER_TRIP_REMINDER_1_HOUR,
  DRIVER_TRIP_REMINDER_24_HOURS,
  CUSTOMER_TRIP_REMINDER_1_HOUR,
  CUSTOMER_TRIP_REMINDER_24_HOURS,
  DRIVER_ON_THE_WAY,
  DRIVER_ON_LOCATION,
  PASSENGER_ON_BOARD,
  TRIP_COMPLETED,
  SHUTTLE_IS_RUNNING_LATE,
  TRIP_CANCELLED_MESSAGE_FOR_DRIVER,
  FLIGHT_TIME_UPDATED_FOR_MEMBERS,
  FLIGHT_TIME_UPDATED_FOR_DRIVER,
  TRIP_CANCELLATION_EMAIL,
  PAYMENT_PROCESSED_EMAIL,
} = {
  SALES_AUTOMATION_ENROLLMENT: "Sales Automation Enrollment",
  SALES_AUTOMATION_1ST_REMINDER: "Sales Automation 1st Reminder",
  SALES_AUTOMATION_2ND_REMINDER: "Sales Automation 2nd Reminder",
  DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER:
    "Driver Pending Job Assignment Reminder",
  DRIVER_TRIP_REMINDER_1_HOUR: "Driver Trip Reminder - 1 Hour",
  DRIVER_TRIP_REMINDER_24_HOURS: "Driver Trip Reminder - 24 Hours",
  CUSTOMER_TRIP_REMINDER_1_HOUR: "Customer Trip Reminder - 1 Hour",
  CUSTOMER_TRIP_REMINDER_24_HOURS: "Customer Trip Reminder - 24 Hours",
  DRIVER_ON_THE_WAY: "Driver On the Way",
  DRIVER_ON_LOCATION: "Driver On-Location",
  PASSENGER_ON_BOARD: "Passenger on Board",
  TRIP_COMPLETED: "Trip Completed",
  SHUTTLE_IS_RUNNING_LATE: "Shuttle is Running Late",
  TRIP_CANCELLED_MESSAGE_FOR_DRIVER: "Trip Cancelled Message for Driver",
  FLIGHT_TIME_UPDATED_FOR_MEMBERS: "Flight time updated for members",
  FLIGHT_TIME_UPDATED_FOR_DRIVER: "Flight time updated for driver",
  TRIP_CANCELLATION_EMAIL: "Trip Cancellation Email",
  PAYMENT_PROCESSED_EMAIL: "Payment Processed",
};

// Custom notification order
const notificationOrder: Record<string, number> = {
  [SALES_AUTOMATION_ENROLLMENT]: 1,
  [SALES_AUTOMATION_1ST_REMINDER]: 2,
  [SALES_AUTOMATION_2ND_REMINDER]: 3,
  [CUSTOMER_TRIP_REMINDER_1_HOUR]: 4,
  [CUSTOMER_TRIP_REMINDER_24_HOURS]: 5,
  [DRIVER_ON_THE_WAY]: 6,
  [DRIVER_ON_LOCATION]: 7,
  [PASSENGER_ON_BOARD]: 8,
  [TRIP_COMPLETED]: 9,
  [SHUTTLE_IS_RUNNING_LATE]: 10,
};

const EXCLUDED_NOTIFICATIONS = [
  DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER,
  DRIVER_TRIP_REMINDER_1_HOUR,
  DRIVER_TRIP_REMINDER_24_HOURS,
  TRIP_CANCELLED_MESSAGE_FOR_DRIVER,
  FLIGHT_TIME_UPDATED_FOR_MEMBERS,
  FLIGHT_TIME_UPDATED_FOR_DRIVER,
];

type UseNotificationSettingsProps = {
  settings?: {
    tripReminderTextTo?: TextRecipientEnum;
    pobStatusTextToBcEnabled?: boolean;
    memberChangedStatusTextEnabled?: boolean;
  };
  driverReminderPendingJobsTextEnabled?: boolean;
  driverReminderTripReminderTextsEnabled?: boolean;
  driverStatusUpdateTextsEnabled?: boolean;
  automatedEmailsEnabled?: boolean;
  tripCancellationEmailEnabled?: boolean;
};

export function useNotificationSettings({
  settings,
  driverReminderPendingJobsTextEnabled,
  driverReminderTripReminderTextsEnabled,
  driverStatusUpdateTextsEnabled,
  automatedEmailsEnabled,
  tripCancellationEmailEnabled,
}: UseNotificationSettingsProps) {
  const {
    tripReminderTextTo,
    pobStatusTextToBcEnabled,
    memberChangedStatusTextEnabled,
  } = settings || {};

  // Query notification settings
  const { data, loading } = useQuery<{
    loadNotificationSettings: NotificationSetting[];
  }>(LOAD_NOTIFICATION_SETTINGS_QUERY, {
    fetchPolicy: "cache-and-network",
  });

  const result = data?.loadNotificationSettings;

  // Filter, exclude, and sort notifications
  const notificationSettings = useMemo(
    () =>
      result
        ? [...result]
            .filter((notification) => {
              return !EXCLUDED_NOTIFICATIONS.includes(
                notification.templateName
              );
            })
            .sort((a, b) => {
              const orderA = notificationOrder[a.templateName] || 999;
              const orderB = notificationOrder[b.templateName] || 999;
              return orderA - orderB;
            })
        : [],
    [result]
  );

  // Separate notifications by channel
  const smsNotifications = useMemo(
    () =>
      notificationSettings.filter(
        (notification) => notification.channel === "SMS"
      ),
    [notificationSettings]
  );

  const emailNotifications = useMemo(
    () =>
      notificationSettings.filter(
        (notification) => notification.channel === "EMAIL"
      ),
    [notificationSettings]
  );

  // Calculate disabled state for each notification setting
  const settingIsDisabledMap = useMemo(() => {
    const tripStatusUpdateDisabled =
      !driverStatusUpdateTextsEnabled && !memberChangedStatusTextEnabled;
    const tripReminderDisabled = tripReminderTextTo === None;

    return {
      [DRIVER_ON_THE_WAY]: tripStatusUpdateDisabled,
      [DRIVER_ON_LOCATION]: tripStatusUpdateDisabled,
      [PASSENGER_ON_BOARD]:
        !pobStatusTextToBcEnabled || tripStatusUpdateDisabled,
      [TRIP_COMPLETED]: tripStatusUpdateDisabled,
      [CUSTOMER_TRIP_REMINDER_1_HOUR]: tripReminderDisabled,
      [CUSTOMER_TRIP_REMINDER_24_HOURS]: tripReminderDisabled,
      [DRIVER_TRIP_REMINDER_1_HOUR]: !driverReminderTripReminderTextsEnabled,
      [DRIVER_TRIP_REMINDER_24_HOURS]: !driverReminderTripReminderTextsEnabled,
      [DRIVER_PENDING_JOB_ASSIGNMENT_REMINDER]:
        !driverReminderPendingJobsTextEnabled,
      [TRIP_CANCELLATION_EMAIL]: !tripCancellationEmailEnabled,
      [PAYMENT_PROCESSED_EMAIL]: !automatedEmailsEnabled,
    };
  }, [
    driverReminderPendingJobsTextEnabled,
    driverReminderTripReminderTextsEnabled,
    pobStatusTextToBcEnabled,
    tripReminderTextTo,
    driverStatusUpdateTextsEnabled,
    memberChangedStatusTextEnabled,
    automatedEmailsEnabled,
    tripCancellationEmailEnabled,
  ]);

  return {
    smsNotifications,
    emailNotifications,
    settingIsDisabledMap,
    loading,
  };
}